import ActionButtonRounded from 'Components/ActionButtonRounded';
import DataTable from 'Components/DataTable';
import { Container, Item } from 'Components/Grid';
import HistoryRowSubHeader from 'Components/HistoryRowSubHeader';
import {
  exists,
  formatTime,
  isPartialDate,
  isTime,
  nowPartialDate,
  partialDateFromDate,
  partialDateToValue,
  sortPartialDate,
} from 'neuro-utils';
import { formatPartialDate } from 'Utility/date';
import * as React from 'react';
import { MyServiceContext } from '..';
import NewTreatmentDecisionCell from './components/treatmentDecisionCell';
import { useAppDispatch as useDispatch, useAppSelector as useSelector } from 'Store/index';
import { actions } from 'Store/myapp/actions';
import ConfirmationDialog from 'Components/ConfirmationDialog';
import colors from '../../../../config/theme/colors';
import { styled } from '@mui/system';
import { informantSurveysByPlatform, msSurveyTypes, surveysByPlatform } from '../config';
import { Clear, Replay, SupervisorAccount } from '@mui/icons-material';
import RecurringTasksList from './components/RecurringTasksList';
import { getNextRecurringDeadline } from 'Routes/MyService/util';
import InputHandler from 'Components/InputHandler';
import ToolTip from 'Components/ToolTip';
import Icon from 'Components/_NewElements/Icon';

const AppointmentData = ({ appointment }: IAppointmentData) => {
  const myServContext = React.useContext(MyServiceContext);
  const { fm } = myServContext;

  const keys: Array<keyof IAppointment> = [
    'startDate',
    'startTime',
    'status',
    'externalAppointmentType',
    'locationGroupId',
    'locationGroupName',
    'locationTypeId',
    'locationTypeText',
    'locationId',
    'locationName',
    'endDate',
    'endTime',
    'comments',
  ];

  return (
    <div>
      {keys.map(key => {
        const localeKey =
          key.substring(0, 8) === 'location' ? key.replace(/id|name|text/i, match => '.' + match.toLowerCase()) : key;

        const rawValue = appointment[key];
        const value: string | null | undefined = exists(rawValue)
          ? isPartialDate(rawValue)
            ? formatPartialDate(rawValue)
            : isTime(rawValue)
              ? formatTime(rawValue)
              : key === 'status'
                ? fm('appointments.opts.' + rawValue)
                : `${rawValue}`
          : undefined;
        return value ? (
          <Container key={key}>
            <Item xs="auto">{fm('appointments.' + localeKey)}</Item>
            <Item xs={true} style={{ textAlign: 'right', fontWeight: 600, paddingLeft: '1rem' }}>
              {value}
            </Item>
          </Container>
        ) : (
          <></>
        );
      })}
    </div>
  );
};

interface IAppointmentData {
  appointment: IAppointment;
}

const ClearIconStyled = styled(Clear)({
  display: 'block',
  width: '3rem',
  color: colors.secondaryText,
  cursor: 'pointer',
  alignSelf: 'center',
  '&:hover': {
    color: 'red',
  },
});

// Check if tasklist end date timestamp has hours and/or minutes added to it or if it's default (date only)
const timestampIsDateOnly = (timestamp: number): boolean => {
  const date = new Date(timestamp);
  return (
    date.getUTCHours() === 0 &&
    date.getUTCMinutes() === 0 &&
    date.getUTCSeconds() === 0 &&
    date.getUTCMilliseconds() === 0
  );
};

const TaskListHistory = ({
  treatmentDecisionDocs,
  taskLists,
}: {
  treatmentDecisionDocs: Array<ININMTTreatmentPeriod>;
  taskLists: Array<ITaskList>;
}): React.JSX.Element => {
  const myServContext = React.useContext(MyServiceContext);
  const { fm, setEditingObj, platform, locale } = myServContext;
  const [showPast, setShowPast] = React.useState<boolean>(false);
  const [loading, setLoading] = React.useState<boolean>(false);

  const patientContactInfo = useSelector((s: { patient: IPatientStore }) => s.patient.contactInfo);
  const orgTaskUISettings = useSelector((s: IState) => s.settings.orgSettings.taskUiSettings);

  const availableTitles = platform ? [...Object.keys(orgTaskUISettings?.[platform] ?? {}), 'other'] : ['other'];

  const historyData: Array<ININMTTreatmentPeriod | ITaskList> = [
    ...(platform === 'ninmt' ? treatmentDecisionDocs : []),
    ...taskLists
      .map(tl => {
        const tlCopy = structuredClone(tl);
        if (tlCopy.end_time && !tlCopy.deadline) tlCopy.deadline = partialDateFromDate(new Date(tlCopy.end_time));
        return tlCopy;
      })
      .filter(tl => {
        return (
          tl.tasks?.every(
            t =>
              platform &&
              (surveysByPlatform[platform]
                ?.flatMap(s =>
                  s === 'pedsql'
                    ? ['pedsqlNeuromuscular', 'pedsqlFatigue']
                    : s === 'treatmentEfficiency'
                      ? ['treatmentEfficiencyBefore', 'treatmentEfficiencyAfter']
                      : s,
                )
                ?.includes(t) ||
                informantSurveysByPlatform[platform]?.includes(t)),
          ) &&
          // Filter out tasklist which titles are not included in the selected platform titles
          availableTitles.includes(tl.title ?? '') &&
          // If task only has deadline or if end_time equals an even date, compare partial dates
          // Else compare milliseconds
          // Enabling showPast overrides comparisons mentioned above
          ((typeof tl.end_time !== 'number' || timestampIsDateOnly(tl.end_time)
            ? partialDateToValue(tl.deadline) >= partialDateToValue(nowPartialDate())
            : Date.now() < tl.end_time) ||
            showPast)
        );
      }),
  ];

  const taskOrgSettings = useSelector((s: IState) => s.settings?.orgSettings?.taskUiSettings);
  const appointments = useSelector((s: IState) => s.appointments.appointments);

  const matchAppointmentToId = (id?: string) => appointments?.find(a => a.id === id);

  const dispatch = useDispatch();

  const [deleteId, setDeleteId] = React.useState<string | null>(null);

  const historyDataToTable = historyData
    .sort((a, b) =>
      sortPartialDate(
        'deadline' in b ? b.deadline : 'date' in b ? b.date : undefined,
        'deadline' in a ? a.deadline : 'date' in a ? a.date : undefined,
      ),
    )
    .map((data, i) => {
      if ('date' in data && data.date && platform === 'ninmt') {
        return <NewTreatmentDecisionCell fm={fm} date={data.date} key={i} />;
      } else if ('deadline' in data || 'recurringTaskDeadline' in data) {
        const sortedTasks =
          platform === 'ms'
            ? data.tasks?.toSorted(
                (a, b) => msSurveyTypes.findIndex(type => type === a) - msSurveyTypes.findIndex(sType => sType === b),
              )
            : data.tasks;
        const tasks = sortedTasks
          ?.map(t => {
            if (t === 'memory' && data.informantId) return fm('myService.parkinson.memoryInformant');
            return fm(`myService.${platform}.opts.${t}`);
          })
          .join(', ');
        const title =
          platform && data?.title && taskOrgSettings?.[platform]?.[data?.title]?.titleLocalized
            ? (taskOrgSettings[platform]?.[data?.title]?.titleLocalized?.[locale] ??
              taskOrgSettings[platform]?.[data?.title]?.titleLocalized?.['fi'])
            : data?.title === 'other'
              ? fm('myService.opts.other')
              : data?.title
                ? data.title
                : '';
        return [
          <div key="deadline" style={{ whiteSpace: 'nowrap', display: 'flex', paddingRight: '0.5rem' }}>
            <div>
              {data.recurring &&
              data.weeklySurveyInterval &&
              data.recurringTaskDeadline &&
              partialDateToValue(data.deadline) >
                partialDateToValue(getNextRecurringDeadline(data.recurringTaskDeadline, data.weeklySurveyInterval))
                ? formatPartialDate(getNextRecurringDeadline(data.recurringTaskDeadline, data.weeklySurveyInterval))
                : formatPartialDate(data.deadline)}
            </div>
            {matchAppointmentToId(data.appointmentId) && (
              <ToolTip
                content={<Icon icon="bell" button />}
                hover
                title={fm('appointments.appointment')}
                description={<AppointmentData appointment={matchAppointmentToId(data.appointmentId)!} />}
              />
            )}
          </div>,
          title,
          tasks ?? '-',
          data.recurring || data.informantId ? (
            <>
              {data.recurring && (
                <ToolTip
                  title={fm('myService.recurring')}
                  hover
                  content={
                    <div style={{ display: 'flex' }}>
                      <Replay
                        fontSize="small"
                        sx={{
                          color:
                            partialDateToValue(data?.deadline) > partialDateToValue(nowPartialDate())
                              ? colors.success
                              : undefined,
                        }}
                      />
                    </div>
                  }
                />
              )}
              {data.informantId && (
                <ToolTip
                  hover
                  title={fm('myService.forRelative')}
                  content={
                    <div style={{ display: 'flex' }}>{data.informantId && <SupervisorAccount fontSize="small" />}</div>
                  }
                />
              )}
            </>
          ) : (
            ''
          ),
          <div key="sendDate" style={{ whiteSpace: 'nowrap' }}>
            {formatPartialDate(data.sendDate)}
          </div>,
          <div key={`${i}${data.id ?? ''}`} style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <ActionButtonRounded
              text="general.edit"
              filled={true}
              width={9.6}
              height={2.5}
              fontSize={16}
              uppercase={false}
              onClick={() => {
                setEditingObj({ type: 'taskList', data: data });
              }}
              disabled={
                data.tasks?.some(task => task.indexOf('treatmentEfficiency') > -1) ||
                !!(data.end_time && Date.now() > data.end_time)
              }
            />
            <ClearIconStyled onClick={() => setDeleteId(data.id)} />
          </div>,
        ];
      } else return 'filter';
    })
    .filter(d => d !== 'filter');

  const recurringTasks = historyData.filter(
    d => 'recurring' in d && d.recurring && partialDateToValue(d?.deadline) > partialDateToValue(nowPartialDate()),
  ) as Array<ITaskList>;

  const hasTaskUISettingsForPlatform = !!(platform && orgTaskUISettings?.[platform]);

  return (
    <>
      <Container style={{ justifyContent: 'space-between' }}>
        <Item>
          <HistoryRowSubHeader header={fm('myService.taskLists')} />
        </Item>
        <Item>
          <ActionButtonRounded
            text="myService.newTaskList"
            filled={true}
            uppercase={false}
            width={9.6}
            height={3}
            fontSize={16}
            onClick={() => {
              setEditingObj({
                type: 'taskList',
                data: null,
              });
            }}
            disabled={!hasTaskUISettingsForPlatform || !!patientContactInfo?.contactNotAllowed}
            disabledTooltip={
              <span>
                {!hasTaskUISettingsForPlatform
                  ? fm('myService.noTasklistSettingsDefined')
                  : fm('myService.newTaskListDisabledTooltip')}
              </span>
            }
          />
        </Item>
      </Container>
      {platform && recurringTasks && recurringTasks.length > 0 && (
        <>
          <Container>
            <Item xs={9} md={7}>
              <RecurringTasksList platform={platform} recurringTasks={recurringTasks} showEndButton />
            </Item>
          </Container>
          <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
            <HistoryRowSubHeader header={fm('myService.all')} fontSize="small" />
            <div style={{ marginRight: '-0.5rem' }}>
              <InputHandler
                type="CheckboxSingle"
                name="showPast"
                editing={true}
                value={showPast ? 'showPast' : ''}
                option={'showPast'}
                showLabel
                formData={{
                  onChange: () => setShowPast(!showPast),
                  document: { showPast: showPast },
                }}
                optionFormatter={() => fm('myService.showPastTasks')}
                labelFirst
              />
            </div>
          </div>
        </>
      )}
      {!recurringTasks ||
        (recurringTasks.length === 0 && (
          <div style={{ display: 'flex', marginRight: '-0.5rem', justifyContent: 'end' }}>
            <InputHandler
              type="CheckboxSingle"
              name="showPast"
              editing={true}
              value={showPast ? 'showPast' : ''}
              option={'showPast'}
              showLabel
              formData={{
                onChange: () => setShowPast(!showPast),
                document: { showPast: showPast },
              }}
              optionFormatter={() => fm('myService.showPastTasks')}
              labelFirst
            />
          </div>
        ))}
      <DataTable
        headers={['deadline', 'taskListTitle', 'surveysAndDiaries', '', 'sendDate', 'hidden']}
        headersFormatter={h => (h === 'hidden' || h === '' ? '' : fm(`myService.${h}`))}
        data={{ rowData: historyDataToTable }}
      />
      <ConfirmationDialog
        open={!!deleteId}
        text={fm('general.reallyWantToDelete')}
        cancel={{ callback: () => setDeleteId(null) }}
        confirm={{
          callback: async () => {
            setLoading(true);
            const deleteList = taskLists.find(d => d.id === deleteId);
            deleteList && (await actions.deleteTasklist(deleteList, dispatch));
            setDeleteId(null);
            setLoading(false);
          },
          loading: loading,
        }}
      />
    </>
  );
};

export default TaskListHistory;
